import { CELL } from "../constants/board.js";
import { TOWER_DEFS } from "../constants/towers.js";
import { getDifficultyModeProfile, normalizeDifficultyMode } from "../constants/difficulty.js";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const sum = (values) => values.reduce((total, value) => total + value, 0);

const towerCenter = (tower) => ({
	x: tower.col * CELL + (CELL / 2),
	y: tower.row * CELL + (CELL / 2),
});

const getPathTouches = (pathPx, center, range) => {
	const rangeSq = range * range;
	const indices = [];

	for (let index = 0; index < pathPx.length; index += 1) {
		const point = pathPx[index];
		const dx = point.x - center.x;
		const dy = point.y - center.y;
		if ((dx * dx) + (dy * dy) <= rangeSq) indices.push(index);
	}

	if (!indices.length) {
		return {
			hitCount: 0,
			passCount: 0,
			coverageRatio: 0,
		};
	}

	let passCount = 1;
	for (let index = 1; index < indices.length; index += 1) {
		if (indices[index] !== indices[index - 1] + 1) passCount += 1;
	}

	return {
		hitCount: indices.length,
		passCount,
		coverageRatio: indices.length / Math.max(1, pathPx.length),
	};
};

const getTowerContribution = (tower, pathPx) => {
	const def = TOWER_DEFS[tower.type];
	const level = tower.level || 0;
	const center = towerCenter(tower);
	const range = def.range[level];
	const damage = def.damage[level];
	const rateMs = def.rate[level];
	const baseDps = damage / Math.max(0.1, rateMs / 1000);
	const touches = getPathTouches(pathPx, center, range);
	const exposure = 0.48 + (touches.coverageRatio * 1.18) + ((touches.passCount - 1) * 0.22);
	const splashFactor = def.splash ? 1 + (def.splash[level] / 95) : 1;
	const chainFactor = def.chains ? 1 + (def.chains[level] * 0.18) : 1;
	const slowFactor = def.slow ? 1 + (def.slow[level] * 0.42) : 1;
	const effectivePower = baseDps * exposure * splashFactor * chainFactor * slowFactor;

	return {
		type: tower.type,
		level,
		range,
		baseDps,
		effectivePower,
		touches,
		antiSwarm: effectivePower * (
			tower.type === "tesla" ? 1.45
				: tower.type === "cannon" ? 1.35
					: tower.type === "blaster" ? 0.95
						: tower.type === "frost" ? 0.85
							: 0.72
		),
		antiArmor: effectivePower * (
			tower.type === "sniper" ? 1.55
				: tower.type === "cannon" ? 0.96
					: tower.type === "tesla" ? 0.84
						: tower.type === "blaster" ? 0.7
							: 0.42
		),
		antiSpeed: effectivePower * (
			tower.type === "frost" ? 1.6
				: tower.type === "blaster" ? 1.05
					: tower.type === "tesla" ? 0.94
						: tower.type === "sniper" ? 0.74
							: 0.7
		),
		antiSupport: effectivePower * (
			tower.type === "sniper" ? 1.2
				: tower.type === "tesla" ? 1.02
					: tower.type === "blaster" ? 0.9
						: tower.type === "cannon" ? 0.86
							: 0.78
		),
		fireCounter: tower.type === "frost" ? effectivePower * 1.45 : effectivePower * 0.18,
		crowdControl: (def.slow ? def.slow[level] * 38 : 0) + (def.chains ? def.chains[level] * 8 : 0) + (def.splash ? def.splash[level] * 0.12 : 0),
		longRange: effectivePower * (range >= 220 ? 1.15 : range >= 170 ? 0.9 : 0.62),
		passCoverage: touches.passCount,
		coverageRatio: touches.coverageRatio,
	};
};

export const createAdaptiveDifficultyState = () => ({
	pressureBias: 0,
	lastAssessment: null,
	lastOutcome: null,
	inWave: null,
	history: [],
});

export const assessPlayerDefense = (state, pathData) => {
	const pathPx = pathData?.px || [];
	const towers = state?.towers || [];
	const contributions = towers.map((tower) => getTowerContribution(tower, pathPx));
	const totalPower = sum(contributions.map((item) => item.effectivePower));
	const antiSwarm = sum(contributions.map((item) => item.antiSwarm));
	const antiArmor = sum(contributions.map((item) => item.antiArmor));
	const antiSpeed = sum(contributions.map((item) => item.antiSpeed));
	const antiSupport = sum(contributions.map((item) => item.antiSupport));
	const fireCounter = sum(contributions.map((item) => item.fireCounter));
	const crowdControl = sum(contributions.map((item) => item.crowdControl));
	const longRange = sum(contributions.map((item) => item.longRange));
	const averageRange = contributions.length
		? sum(contributions.map((item) => item.range * item.effectivePower)) / Math.max(1, totalPower)
		: 0;
	const averagePasses = contributions.length
		? sum(contributions.map((item) => item.passCoverage)) / contributions.length
		: 0;
	const averageCoverage = contributions.length
		? sum(contributions.map((item) => item.coverageRatio)) / contributions.length
		: 0;
	const towersByType = contributions.reduce((accumulator, item) => {
		accumulator[item.type] = (accumulator[item.type] || 0) + 1;
		return accumulator;
	}, {});
	const diversity = Object.keys(towersByType).length;
	const upgradeCount = sum((state?.towers || []).map((tower) => tower.level || 0));
	const waveNumber = (state?.wave || 0) + 1;
	const reservePressure = clamp(((state?.gold || 0) - (110 + (waveNumber * 7))) / 180, -0.3, 1.8);
	const towerMomentum = clamp((towers.length * 0.12) + (upgradeCount * 0.06) + (diversity * 0.04), 0, 2.4);
	const firepowerScore = totalPower / Math.max(32, 46 + (waveNumber * 7.4));
	const controlScore = crowdControl / Math.max(18, 30 + (waveNumber * 3.2));
	const strategyScore = (
		(firepowerScore * 0.56)
		+ (reservePressure * 0.18)
		+ (towerMomentum * 0.12)
		+ (averageCoverage * 0.14)
		+ (averagePasses * 0.06)
		+ (controlScore * 0.1)
	);
	const armorGap = clamp(1 - (antiArmor / Math.max(36, totalPower * 0.52)), 0, 1.2);
	const swarmGap = clamp(1 - (antiSwarm / Math.max(40, totalPower * 0.72)), 0, 1.2);
	const speedGap = clamp(1 - ((antiSpeed + crowdControl) / Math.max(34, totalPower * 0.48)), 0, 1.2);
	const supportGap = clamp(1 - (antiSupport / Math.max(28, totalPower * 0.42)), 0, 1.2);
	const fireGap = clamp(1 - (fireCounter / Math.max(16, totalPower * 0.22)), 0, 1.2);
	const longRangeGap = clamp(1 - (longRange / Math.max(26, totalPower * 0.3)), 0, 1.2);

	return {
		totalPower: Number(totalPower.toFixed(2)),
		firepowerScore: Number(firepowerScore.toFixed(2)),
		controlScore: Number(controlScore.toFixed(2)),
		strategyScore: Number(strategyScore.toFixed(2)),
		averageRange: Number(averageRange.toFixed(1)),
		averagePasses: Number(averagePasses.toFixed(2)),
		averageCoverage: Number(averageCoverage.toFixed(2)),
		antiSwarm: Number(antiSwarm.toFixed(2)),
		antiArmor: Number(antiArmor.toFixed(2)),
		antiSpeed: Number(antiSpeed.toFixed(2)),
		antiSupport: Number(antiSupport.toFixed(2)),
		fireCounter: Number(fireCounter.toFixed(2)),
		longRange: Number(longRange.toFixed(2)),
		reservePressure: Number(reservePressure.toFixed(2)),
		towerMomentum: Number(towerMomentum.toFixed(2)),
		towerCount: towers.length,
		upgradeCount,
		diversity,
		towersByType,
		gaps: {
			armor: Number(armorGap.toFixed(2)),
			swarm: Number(swarmGap.toFixed(2)),
			speed: Number(speedGap.toFixed(2)),
			support: Number(supportGap.toFixed(2)),
			fire: Number(fireGap.toFixed(2)),
			longRange: Number(longRangeGap.toFixed(2)),
		},
	};
};

export const buildAdaptiveWaveContext = (state, pathData) => {
	const mode = normalizeDifficultyMode(state?.difficultyMode);
	const modeProfile = getDifficultyModeProfile(mode);
	const adaptiveState = state?.adaptiveDifficulty || createAdaptiveDifficultyState();
	const assessment = assessPlayerDefense(state, pathData);
	const targetScore = 0.79 + ((state?.wave || 0) * (state?.runMode === "survival" ? 0.052 : 0.044));
	const rawBias = clamp((assessment.strategyScore - targetScore) * 0.34, -0.25, 0.5);
	const pressureBias = clamp(
		(adaptiveState.pressureBias || 0) * 0.58
		+ (rawBias * modeProfile.adaptiveSensitivity)
		+ (Math.max(0, assessment.reservePressure) * 0.14),
		-0.26,
		0.66,
	);
	const weaknessBoost = {
		swarm: 1 + (assessment.gaps.swarm * 1.16) + Math.max(0, pressureBias * 0.26),
		speed: 1 + (assessment.gaps.speed * 1.12) + (assessment.averageRange < 150 ? 0.14 : 0),
		armor: 1 + (assessment.gaps.armor * 1.26) + (assessment.upgradeCount < state.towers.length ? 0.11 : 0),
		support: 1 + (assessment.gaps.support * 1.06) + (assessment.gaps.longRange * 0.22),
		boss: 1 + (assessment.gaps.longRange * 1.05) + (assessment.gaps.armor * 0.32) + Math.max(0, pressureBias * 0.38),
	};
	const budgetMultiplier = clamp(
		modeProfile.waveBudgetMultiplier
		* (1 + (pressureBias * 1.08) + (Math.max(0, assessment.reservePressure) * 0.22) - (assessment.averagePasses - 1) * 0.02),
		0.84,
		1.96,
	);
	const bodyCapMultiplier = clamp(
		modeProfile.bodyCapMultiplier
		* (1 + (pressureBias * 0.58) + (assessment.gaps.swarm * 0.12) - (assessment.averagePasses - 1) * 0.04),
		0.88,
		1.44,
	);

	return {
		mode,
		modeProfile,
		assessment,
		pressureBias: Number(pressureBias.toFixed(2)),
		budgetMultiplier: Number(budgetMultiplier.toFixed(2)),
		bodyCapMultiplier: Number(bodyCapMultiplier.toFixed(2)),
		tagWeights: Object.fromEntries(
			Object.entries(weaknessBoost).map(([key, value]) => [key, Number(clamp(value, 0.86, 2.4).toFixed(2))]),
		),
	};
};

export const beginAdaptiveWave = (state, pathData) => {
	const context = buildAdaptiveWaveContext(state, pathData);
	state.adaptiveDifficulty = state.adaptiveDifficulty || createAdaptiveDifficultyState();
	state.adaptiveDifficulty.lastAssessment = context;
	state.adaptiveDifficulty.inWave = {
		waveNumber: (state.wave || 0) + 1,
		livesStart: state.lives,
		goldStart: state.gold,
		towersStart: state.towers.length,
		towersBuiltStart: state.stats?.towersBuilt || 0,
		towersUpgradedStart: state.stats?.towersUpgraded || 0,
		goldSpentStart: state.stats?.goldSpent || 0,
		startedAt: Date.now(),
	};
	return context;
};

export const finalizeAdaptiveWave = (state) => {
	const adaptiveState = state?.adaptiveDifficulty;
	const inWave = adaptiveState?.inWave;
	if (!adaptiveState || !inWave) return;

	const leaks = Math.max(0, inWave.livesStart - state.lives);
	const built = Math.max(0, (state.stats?.towersBuilt || 0) - inWave.towersBuiltStart);
	const upgraded = Math.max(0, (state.stats?.towersUpgraded || 0) - inWave.towersUpgradedStart);
	const spent = Math.max(0, (state.stats?.goldSpent || 0) - inWave.goldSpentStart);
	const goldDelta = state.gold - inWave.goldStart;
	const reserve = state.gold;
	let adjustment = 0;

	if (leaks > 0) adjustment -= Math.min(0.2, leaks * 0.08);
	if (state.lives <= 6) adjustment -= 0.08;
	if (built === 0 && upgraded <= 1 && reserve >= 150) adjustment += 0.16;
	if (goldDelta > 70 && reserve >= 190) adjustment += 0.13;
	if (spent < 70 && reserve >= 210) adjustment += 0.1;
	if (spent < 45 && reserve >= 250) adjustment += 0.08;
	if (built >= 2 || upgraded >= 2) adjustment -= 0.015;

	adaptiveState.pressureBias = clamp(((adaptiveState.pressureBias || 0) * 0.64) + adjustment, -0.3, 0.68);
	adaptiveState.lastOutcome = {
		waveNumber: inWave.waveNumber,
		leaks,
		built,
		upgraded,
		spent,
		goldDelta,
		reserve,
		durationMs: Date.now() - inWave.startedAt,
		pressureBias: Number(adaptiveState.pressureBias.toFixed(2)),
	};
	adaptiveState.history = [...(adaptiveState.history || []).slice(-5), adaptiveState.lastOutcome];
	adaptiveState.inWave = null;
};
